// ============================================================
// The status line under an attack in the combat log - one sentence
// saying how much the attack took and what is left.
//
// Two log renderers print it (the classic `NarrativeEntry` and the
// collapsible `NarrativeEntryStructured`), and they must agree word for
// word: a player who switches views should read the same verdict. Both
// honour the banded-wounds house rule through `showNumbers`, which
// swaps every number for the words a person at the table would use.
// ============================================================
import { damagePhrase } from './survival/damagePhrase';
import { healthBandLabel } from './healthRamp';

/** A wound tally: what the attack dealt, and the wounds left of `max`. */
export interface WoundsTally {
  dealt: number;
  after: number;
  max?: number;
}

/** A horde's magnitude tally; `destroyed` is the renderer's own verdict. */
export interface MagnitudeTally {
  dealt: number;
  after?: number;
  max?: number;
  destroyed: boolean;
}

/** What is left after the wounds: a count, a warning at zero, or a band. */
function woundsLeft({ after, max }: WoundsTally, showNumbers: boolean): string {
  if (!showNumbers) return after < 0 ? 'CRITICAL!' : healthBandLabel(after, max || 0);
  // The severity IS the wound count past zero, so it goes with the number.
  if (after < 0) return 'CRITICAL! Severity ' + Math.abs(after);
  if (after === 0) return 'At 0 wounds - next hit is Critical!';
  return 'Remaining: ' + after + '/' + max;
}

/**
 * "Total: 8 wounds. Remaining: 7/15", or with the numbers hidden
 * "a serious wound. Bloodied". A missing `max` reads as zero for the words.
 */
export function woundsSummary(tally: WoundsTally, showNumbers: boolean): string {
  const lead = showNumbers ? `Total: ${tally.dealt} wounds. ` : `${damagePhrase(tally.dealt, tally.max || 0)}. `;
  return lead + woundsLeft(tally, showNumbers);
}

/** What is left of the horde: destroyed, a count, or a band. */
function magnitudeLeft({ after, max, destroyed }: MagnitudeTally, showNumbers: boolean): string {
  if (destroyed) return 'HORDE DESTROYED!';
  if (showNumbers) return 'Magnitude: ' + after + '/' + max;
  return healthBandLabel(after || 0, max || 0);
}

/**
 * "Magnitude damage: 5. Magnitude: 20/30", or with the numbers hidden
 * "The mob thins - a solid hit. Bloodied". Missing figures read as zero
 * for the words.
 */
export function magnitudeSummary(tally: MagnitudeTally, showNumbers: boolean): string {
  const lead = showNumbers
    ? `Magnitude damage: ${tally.dealt}. `
    : `The mob thins - ${damagePhrase(tally.dealt, tally.max || 0)}. `;
  return lead + magnitudeLeft(tally, showNumbers);
}
